import {
  ArrowNarrowUpRightIcon,
  Edit01Icon,
} from '@langchain/untitled-ui-icons';

import { useEffect, useState } from 'react';

import { EditableText } from '@/components/EditableText/EditableText';
import { TruncatedTextWithTooltip } from '@/components/TruncatedTextWithTooltip.tsx';
import { Button, IconButton, Tooltip } from '@/design-system';
import { cn } from '@/utils/tailwind';

/**
 * A generalized component for editing text values with a pencil icon.
 * When clicked, it transforms into an editable text field.
 */
export function EditableValue({
  value,
  onSubmit,
  onError,
  tooltipTitle = 'Edit value',
  textClassProp,
  pencilClassProp,
  inputTextClassProp,
  onValueClick,
  valueClickTooltipTitle,
  readOnly = false,
}: {
  value: string;
  onSubmit: (newValue: string) => void;
  onError?: () => void;
  tooltipTitle?: string;
  textClassProp?: string;
  pencilClassProp?: string;
  inputTextClassProp?: string;
  onValueClick?: () => void;
  valueClickTooltipTitle?: string;
  readOnly?: boolean;
}) {
  const [isEditing, setIsEditing] = useState(false);
  const [currentValue, setCurrentValue] = useState(value);

  useEffect(() => {
    setCurrentValue(value);
  }, [value]);

  const textClass = textClassProp
    ? `border-[6px] border-transparent` // Only include non-conflicting styles when custom class is provided
    : `text-sm text-secondary font-medium max-w-[200px] truncate border-[6px] border-transparent`;

  return (
    <div className="flex flex-row gap-2">
      {!readOnly && isEditing ? (
        <EditableText
          value={currentValue}
          onEdit={setCurrentValue}
          onSubmit={(newText) => {
            try {
              onSubmit(newText);
            } catch (error) {
              onError?.();
            }
          }}
          isEditing={isEditing}
          setIsEditing={setIsEditing}
          className={cn(textClass, inputTextClassProp)}
        />
      ) : onValueClick ? (
        <Tooltip title={valueClickTooltipTitle}>
          <Button
            onClick={onValueClick}
            rightDecorator={ArrowNarrowUpRightIcon}
            variant="outlined"
            color="secondary"
            aria-label={valueClickTooltipTitle}
          >
            <TruncatedTextWithTooltip
              text={currentValue}
              className={cn(textClass, textClassProp)}
            />
          </Button>
        </Tooltip>
      ) : (
        <TruncatedTextWithTooltip
          text={currentValue}
          className={cn(textClass, textClassProp)}
        />
      )}
      {!readOnly && !isEditing && (
        <IconButton
          icon={Edit01Icon}
          label={tooltipTitle}
          onClick={() => setIsEditing(true)}
          variant="plain"
          color="secondary"
          className={pencilClassProp}
        />
      )}
    </div>
  );
}
